import { useEffect, useRef, useCallback } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'

type EventHandler = (...args: unknown[]) => void

export interface KnowledgeBasePopulatedNotification {
  clientCompanyId: string
  status: 'Completed' | 'Failed'
  populatedCount: number
  skippedCount: number
  failedCount: number
  errorMessage?: string | null
}

interface UseKnowledgeBaseSignalROptions {
  userId: string | undefined
  onPopulateCompleted: (notification: KnowledgeBasePopulatedNotification) => void
  onPopulateFailed: (notification: KnowledgeBasePopulatedNotification) => void
}

/**
 * Subscribes to the SignalR "KnowledgeBasePopulated" event on the user's group.
 * The server sends this after the service bus orchestration finishes AI population.
 */
export function useKnowledgeBaseSignalR({
  userId,
  onPopulateCompleted,
  onPopulateFailed,
}: UseKnowledgeBaseSignalROptions) {
  const handlerRef = useRef<{ handler: EventHandler; group: string } | null>(null)

  const cleanup = useCallback(() => {
    const entry = handlerRef.current
    if (entry) {
      signalRService.off('KnowledgeBasePopulated', entry.handler)
      signalRService.leaveGroup(entry.group)
      handlerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    let cancelled = false

    const group = `user-${userId}`

    signalRService.joinGroup(group).then(() => {
      if (cancelled) {
        signalRService.leaveGroup(group)
        return
      }

      const handler: EventHandler = (payload: unknown) => {
        const notification = payload as KnowledgeBasePopulatedNotification
        if (notification.status === 'Completed') {
          onPopulateCompleted(notification)
        } else {
          onPopulateFailed(notification)
        }
      }

      signalRService.on('KnowledgeBasePopulated', handler)
      handlerRef.current = { handler, group }
    }).catch((err) => {
      console.error(`Failed to join SignalR group "${group}":`, err)
    })

    return () => {
      cancelled = true
      cleanup()
    }
  }, [userId, onPopulateCompleted, onPopulateFailed, cleanup])
}
